/**
 * Repair helpers for malformed Azure operation identities.
 *
 * Otto's `/apiOperations` endpoint sometimes returns operations whose
 * `properties.api.id` is truncated to `/automationProjects/{p}/managedApis/`
 * with the connector slug stripped (and `api.name` empty), and whose
 * `op.id` is the matching `/managedApis//{opName}` form. The shapes below
 * detect and rewrite both to their canonical forms by joining against the
 * reliable `/managedApis` connector list, with a fallback to a curated
 * canonical-displayName map for popular brands whose `/managedApis` entry
 * may be absent in a given tenant.
 *
 * Without this repair, malformed ops collide catastrophically during merge
 * dedupe (common op names overwrite each other across brands), and search
 * groups them under a `displayName:` fallback key — which renders but
 * breaks drill-in fetches (slug extraction yields `''` → 404), tier-aware
 * ranking, and the connector-id cache key.
 */

import type { Connector, DiscoveryOperation } from '@/services/operationsService';
import { CANONICAL_DISPLAY_NAMES } from '@/services/searchOperationsService';
import { toShortManagedApiId } from '@/utils/connections';

/** `/managedApis/` or `/managedApis` (trailing slash optional). */
export const isMalformedManagedApiId = (id: string | undefined | null): boolean => {
  if (!id) return true;
  return /\/managedApis\/?$/i.test(id.trim());
};

/**
 * Matches both `…/managedApis/` (trailing slash) and
 * `…/managedApis//{opName}` (empty connector segment).
 */
export const isMalformedOpId = (id: string | undefined | null): boolean => {
  if (!id) return true;
  return /\/managedApis\/\/?($|[^/])/i.test(id.trim());
};

/**
 * Build an exact-match `displayName.toLowerCase() → {shortId, name}` index
 * from a `/managedApis` connector list. When two connectors collide on
 * displayName, the shortest `name` wins (canonical-over-fork tiebreak); ties
 * on length fall back to `localeCompare` so the chosen winner is
 * deterministic and independent of input order. Mirrors the dedup ordering
 * in `mergeDuplicateDisplayNames` (`searchOperationsService.ts`).
 * `shortId` is canonicalized via `toShortManagedApiId` so downstream code
 * works against a stable `/managedApis/{slug}` value.
 */
export const buildConnectorIdentityIndex = (
  connectors: Connector[] | undefined,
): Map<string, { id: string; name: string }> => {
  const map = new Map<string, { id: string; name: string }>();
  if (!connectors) return map;
  for (const c of connectors) {
    const dn = c.properties?.displayName?.trim().toLowerCase();
    const id = c.id;
    if (!dn || !id) continue;
    const name = c.name ?? '';
    const existing = map.get(dn);
    // Replace only when the new connector is a strictly better canonical
    // candidate: shorter slug wins, then alphabetic `localeCompare` breaks
    // equal-length ties so identity repair is order-independent.
    const shouldReplace =
      !existing ||
      name.length < existing.name.length ||
      (name.length === existing.name.length &&
        name.localeCompare(existing.name) < 0);
    if (shouldReplace) {
      map.set(dn, { id: toShortManagedApiId(id).toLowerCase(), name });
    }
  }
  return map;
};

/**
 * Pure, idempotent repair: rewrite a malformed Azure op's `api.id` /
 * `api.name` (and `op.id` when malformed) to canonical values from the
 * connector identity index, falling back to {@link CANONICAL_DISPLAY_NAMES}
 * for popular brands whose `/managedApis` entry is missing. Returns the
 * original op unchanged when the id is well-formed, the displayName is
 * unusable, no canonical entry exists, or the repair would be a no-op.
 */
export const repairAzureOpIdentity = (
  op: DiscoveryOperation,
  index: Map<string, { id: string; name: string }>,
): DiscoveryOperation => {
  const api = op.properties?.api;
  if (!api) return op;
  if (!isMalformedManagedApiId(api.id)) return op;
  const key = api.displayName?.trim().toLowerCase();
  if (!key) return op;

  let repaired = index.get(key);
  if (!repaired) {
    const canonicalSlug = CANONICAL_DISPLAY_NAMES.get(key);
    if (canonicalSlug) {
      repaired = { id: `/managedapis/${canonicalSlug}`, name: canonicalSlug };
    }
  }
  if (!repaired) return op;

  const opIdNeedsRepair = isMalformedOpId(op.id);
  const nextOpId =
    opIdNeedsRepair && op.name
      ? `/managedApis/${repaired.name}/apiOperations/${op.name}`
      : op.id;

  if (repaired.id === (api.id ?? '').toLowerCase() && nextOpId === op.id) {
    return op;
  }

  return {
    ...op,
    id: nextOpId,
    properties: {
      ...op.properties!,
      api: { ...api, id: repaired.id, name: repaired.name },
    },
  };
};
